<template>
  <div class="problem-detail-page">
    <a-card class="problem-detail" size="small" :bordered="false" :loading="loading">
      <div class="problem-detail__header">
        <a-page-header :sub-title="`问题详情 # PB-${problemId}`">
          <template slot="title">
            <div v-if="problemData.is_fault" class="corner" @click="onViewFault" />
            <span>{{ problemData.title }}</span>
          </template>
          <template slot="extra">
            <a-button type="primary" icon="edit" @click="onEdit()"> 编辑 </a-button>
          </template>
        </a-page-header>
      </div>
      <div class="problem-detail__content">
        <div class="problem-detail__main">
          <a-tabs v-model="tabsActiveKey" size="small">
            <a-tab-pane key="1" tab="问题详情">
              <div class="problem-content">
                <div v-if="problemData.summary" class="info-group">
                  <div class="info-group__title">问题总结</div>
                  <div class="info-group__content">
                    <vditor-preview :content="problemData.summary" />
                  </div>
                </div>
                <div class="info-group">
                  <div class="info-group__title">问题描述</div>
                  <div class="info-group__content">
                    <vditor-preview :content="problemData.content" />
                  </div>
                </div>
              </div>
            </a-tab-pane>
            <a-tab-pane key="2" tab="变更日志" force-render> <problem-record ref="record" :source-id="problemId" /> </a-tab-pane>
          </a-tabs>
          <a-collapse v-model="activeKeys" :bordered="false">
            <template #expandIcon="props">
              <a-icon type="caret-right" :rotate="props.isActive ? 90 : 0" />
            </template>
            <a-collapse-panel key="1" class="problem-panel">
              <span slot="header">
                <span>评论</span> <a-button type="link" class="collapse-button" @click.stop="onAddComment"> <a-icon type="plus" /> 添加 </a-button>
              </span>
              <problem-comment ref="comment" :source-id="problemId" source-type="problem" />
            </a-collapse-panel>
          </a-collapse>
        </div>
        <div class="problem-detail__sidebar">
          <a-card size="small" class="problem-info" :bordered="false">
            <div class="info-group">
              <div class="info-group__title">问题信息</div>
              <a-descriptions size="small" :column="1">
                <a-descriptions-item label="当前状态">
                  <problem-status-edit :source-id="problemData.id" :source-value="problemData.status" :status-flow="problemData.status_flow" @change="onReload" />
                </a-descriptions-item>
                <a-descriptions-item label="问题类型">{{ problemData.problem_type ? problemTypes[problemData.problem_type].label : '--' }}</a-descriptions-item>
                <a-descriptions-item label="紧急程度">
                  <a-tag v-if="problemData.urgency" :color="problemUrgencys[problemData.urgency].color">{{ problemUrgencys[problemData.urgency].label }}</a-tag>
                  <span v-else>--</span>
                </a-descriptions-item>
                <a-descriptions-item label="出现频率"> {{ problemData.frequency ? problemFrequencys[problemData.frequency].label : '--' }} </a-descriptions-item>
                <a-descriptions-item label="关联项目">
                  <a-tag v-for="(item, index) in problemData.projects" :key="index" color="pink">{{ item.name }}</a-tag>
                </a-descriptions-item>
                <a-descriptions-item label="反馈来源">{{ problemData.channel ? problemChannels[problemData.channel].label : '--' }}</a-descriptions-item>
              </a-descriptions>
            </div>
            <div class="info-group">
              <div class="info-group__title">处理信息</div>
              <a-descriptions size="small" :column="1">
                <a-descriptions-item label="反馈人"> {{ getUser(problemData.reporter) }} </a-descriptions-item>
                <a-descriptions-item label="处理人"> {{ getUser(problemData.handler) }} </a-descriptions-item>
                <a-descriptions-item label="解决人"> {{ getUser(problemData.solver) }} </a-descriptions-item>
                <a-descriptions-item label="创建人"> {{ getUser(problemData.creator) }} </a-descriptions-item>
              </a-descriptions>
            </div>
            <div class="info-group">
              <div class="info-group__title">时间信息</div>
              <a-descriptions size="small" :column="1">
                <a-descriptions-item label="上报时间"> {{ problemData.reported_time || '--' }} </a-descriptions-item>
                <a-descriptions-item label="开始时间"> {{ problemData.started_time || '--' }} </a-descriptions-item>
                <a-descriptions-item label="解决时间"> {{ problemData.resolved_time || '--' }} </a-descriptions-item>
                <a-descriptions-item label="发现时间"> {{ problemData.discovered_time || '--' }} </a-descriptions-item>
                <a-descriptions-item label="定位时间"> {{ problemData.located_time || '--' }} </a-descriptions-item>
                <a-descriptions-item label="业务确认时间"> {{ problemData.confirmed_time || '--' }} </a-descriptions-item>
                <a-descriptions-item label="创建时间"> {{ problemData.created_time }} </a-descriptions-item>
                <a-descriptions-item label="更新时间"> {{ problemData.updated_time }} </a-descriptions-item>
              </a-descriptions>
            </div>
          </a-card>
        </div>
      </div>
    </a-card>
    <problem-modal ref="problemModal" @close="close" />
  </div>
</template>

<script>
import { problemDetail } from '@/api/event/problem';

import VditorPreview from '@/components/VditorEditor/preview';
import ProblemModal from '@/pages/event/components/ProblemModal';
import ProblemRecord from '@/pages/event/components/ProblemRecord';
import ProblemComment from '@/pages/event/components/ProblemComment';
import ProblemStatusEdit from '@/pages/event/components/ProblemStatusEdit';
import { problemUrgencys, problemChannels, problemFrequencys, problemTypes, problemStatus } from '@/utils/dictionary';

export default {
  components: { VditorPreview, ProblemModal, ProblemRecord, ProblemComment, ProblemStatusEdit },
  data() {
    return {
      problemTypes,
      problemStatus,
      problemUrgencys,
      problemChannels,
      problemFrequencys,
      loading: true,
      problemData: {
        projects: []
      },
      tabsActiveKey: '1',
      activeKeys: ['1'],
      statusOptions: [],
      problemId: parseInt(this.$route.query.problemId) || null
    };
  },
  computed: {
    getUser() {
      return user => {
        return user ? user.nickname : '--';
      };
    }
  },
  created() {
    this.getProblemDetail();
  },

  methods: {
    // 获取问题详情
    getProblemDetail() {
      problemDetail({ id: this.problemId })
        .then(res => {
          this.problemData = res.data;
          const statusFlow = res.data.status_flow;
          this.statusOptions = [...statusFlow.child_node, ...statusFlow.turn_down_node];
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },

    onEdit() {
      this.$nextTick(() => {
        this.$refs.problemModal.openEdit(this.problemId);
      });
    },

    onReload() {
      this.getProblemDetail();
      this.$nextTick(() => {
        this.$refs['comment'].getCommentList();
        this.$refs['record'].getProblemRecord();
      });
    },

    onAddComment() {
      if ('1'.includes(this.activeKeys) === false) {
        this.activeKeys.push('1');
      }
      this.$refs['comment'].onAddComment();
    },

    // 查看故障详情
    onViewFault() {
      this.$router.push({
        name: 'page:event:fault:detail',
        query: { faultId: this.problemId }
      });
    },

    // 关闭弹窗
    close() {
      this.getProblemDetail();
      this.$nextTick(() => {
        this.$refs['comment'].getCommentList();
        this.$refs['record'].getProblemRecord();
      });
    }
  }
};
</script>

<style lang="less" scoped>
:deep(.ant-page-header) {
  padding: 8px 16px;
}

.problem-detail-page {
  background-color: #f0f2f5;
}

.problem-detail {
  border-radius: 8px;
  :deep(.ant-card-body) {
    padding: 0;
  }

  &__header {
    padding: 8px 16px;
    border-bottom: 1px solid #f0f0f0;
    position: relative;

    :deep(.ant-page-header) {
      padding: 0;
    }
  }

  &__content {
    display: flex;
    gap: 16px;
    padding: 0 16px 16px 16px;
  }

  &__main {
    flex: 1;
    min-width: 0;
  }

  &__sidebar {
    width: 320px;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
}

.problem-summary {
  margin-bottom: 16px;
  padding: 16px;
  background: #fff;
  border-radius: 4px;
  border: 1px solid #f0f0f0;

  &__title {
    color: rgba(0, 0, 0, 0.85);
    font-size: 14px;
    font-weight: 500;
    margin-bottom: 12px;
  }

  &__content {
    color: rgba(0, 0, 0, 0.85);
    font-size: 13px;
    line-height: 22px;
    word-break: break-all;
    white-space: pre-wrap;
  }
}

.problem-info {
  background-color: #fafafa;
  border-radius: 8px;

  :deep(.ant-card-body) {
    padding: 16px;
  }

  .info-group {
    & + .info-group {
      margin-top: 16px;
    }

    &__title {
      margin-bottom: 12px;
      color: rgba(0, 0, 0, 0.85);
      font-size: 14px;
      font-weight: 500;
      position: relative;
      padding-left: 12px;

      &::before {
        content: '';
        position: absolute;
        left: 0;
        top: 50%;
        transform: translateY(-50%);
        width: 4px;
        height: 16px;
        background-color: #1890ff;
        border-radius: 2px;
      }
    }
  }

  :deep(.ant-descriptions-item) {
    padding-bottom: 16px;
    display: flex;

    &:last-child {
      padding-bottom: 0;
    }

    .ant-descriptions-item-label {
      color: rgba(0, 0, 0, 0.45);
      font-size: 13px;
      line-height: 28px;
      text-align: right;
      padding-right: 4px;
      width: 96px;
      white-space: nowrap;
      flex: none;
    }

    .ant-descriptions-item-content {
      color: rgba(0, 0, 0, 0.85);
      font-size: 13px;
      line-height: 28px;
      flex: 1;
      min-width: 0;
      word-break: break-all;
    }
  }

  :deep(.ant-descriptions-row) {
    display: block;
  }
}

:deep(.ant-page-header-footer) {
  margin-top: 0;

  .ant-tabs-bar .ant-tabs-nav .ant-tabs-tab {
    font-size: 14px;
    font-weight: bold;
  }
}

.problem-panel {
  border-radius: 4px;
  border: 0;
  background: #fff;
  overflow: hidden;
}

.problem-content {
  background-color: #fafafa;
  padding: 16px;
  border-radius: 8px;

  .info-group {
    & + .info-group {
      margin-top: 16px;
    }

    &__title {
      margin-bottom: 12px;
      color: rgba(0, 0, 0, 0.85);
      font-size: 14px;
      font-weight: 500;
      position: relative;
      padding-left: 12px;

      &::before {
        content: '';
        position: absolute;
        left: 0;
        top: 50%;
        transform: translateY(-50%);
        width: 4px;
        height: 16px;
        background-color: #1890ff;
        border-radius: 2px;
      }
    }

    &__content {
      color: rgba(0, 0, 0, 0.85);
      font-size: 13px;
      line-height: 22px;
    }
  }
}

.collapse-button {
  color: #86909c;
}

.corner {
  position: absolute;
  top: 0px;
  left: 0px;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.4), transparent) #ec374d;
  z-index: 99;
}

.corner::before {
  position: absolute;
  top: 0;
  left: -4px;
  font-size: 14px;
  height: 20px;
  width: 48px;
  text-align: center;
  content: 'RCA';
  border-radius: 2px 2px 2px 0px;
  padding: 2px 4px;
  line-height: 14px;
  color: #fff;
  z-index: 99;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.4), transparent) #ec374d;
  background-blend-mode: soft-light;
}

.corner::after {
  position: absolute;
  content: '';
  left: -4px;
  clip-path: polygon(0 0, 5px 0, 5px 5px);
  width: 12px;
  height: 12px;
  top: 20px;
  background: linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3));
  background-color: inherit;
  z-index: 99;
}
</style>
